import {
    Download,
    Eye,
    Pencil,
    RefreshCw,
    Replace,
    Trash2,
} from "lucide-react";
import { useState } from "react";

import type { OutputTemplateListItem } from "@/api";
import {
    RowActionsMenu,
    type RowAction,
} from "@/components/shared/RowActionsMenu";

import { DeleteTemplateDialog } from "./DeleteTemplateDialog";
import { RenameTemplateDialog } from "./RenameTemplateDialog";
import { UploadTemplateDialog } from "./UploadTemplateDialog";
import { displayStatus } from "./useOutputTemplates";
import { useOpenOutputTemplateOriginal } from "./useOpenOutputTemplateOriginal";

interface TemplateActionsMenuProps {
    template: OutputTemplateListItem;
    onRetry: (id: number) => void;
}

export function TemplateActionsMenu({
    template,
    onRetry,
}: TemplateActionsMenuProps) {
    const [renameOpen, setRenameOpen] = useState(false);
    const [replaceOpen, setReplaceOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);

    const originalFile = useOpenOutputTemplateOriginal(template.id);

    const status = displayStatus(template);
    const canRetry = status === "failed";
    const isSpreadsheet = template.file_type === "xlsx";

    const actions: RowAction[] = [
        {
            key: "rename",
            label: "Rename",
            icon: <Pencil />,
            onSelect: () => setRenameOpen(true),
        },
        {
            key: "replace",
            label: "Replace",
            icon: <Replace />,
            onSelect: () => setReplaceOpen(true),
        },
        {
            key: "open-original",
            label: isSpreadsheet ? "Download original" : "View original",
            icon: isSpreadsheet ? <Download /> : <Eye />,
            onSelect: originalFile.openOriginal,
            disabled: originalFile.isPending,
        },
        ...(canRetry
            ? [
                  {
                      key: "retry",
                      label: "Re-roll compile",
                      icon: <RefreshCw />,
                      onSelect: () => onRetry(template.id),
                  } satisfies RowAction,
              ]
            : []),
        {
            key: "delete",
            label: "Delete",
            icon: <Trash2 />,
            variant: "destructive",
            onSelect: () => setDeleteOpen(true),
        },
    ];

    return (
        <>
            <RowActionsMenu
                actions={actions}
                ariaLabel="Open template actions menu"
                disabled={originalFile.isPending}
            />

            <RenameTemplateDialog
                open={renameOpen}
                templateId={template.id}
                currentName={template.name}
                onClose={() => setRenameOpen(false)}
            />
            <UploadTemplateDialog
                open={replaceOpen}
                templateId={template.id}
                onClose={() => setReplaceOpen(false)}
            />
            <DeleteTemplateDialog
                open={deleteOpen}
                templateId={template.id}
                templateName={template.name}
                onClose={() => setDeleteOpen(false)}
            />
        </>
    );
}
